import { Crosshair } from 'lucide-react';
import { VENUE_STATE_LABEL, type VenueReadiness } from './venueReadiness';

/** One line per venue that cannot show rows right now (#904). Rendered above
 * the summary tiles so the figures below are read with their coverage. The
 * readable venue's data is untouched; this is an explanation, not a gate.
 *
 * A venue still loading is not named (#1060): its figures already read "—",
 * and a line per pending read made the box grow, shrink and vanish above the
 * tiles as each venue answered. Only a settled answer (not configured,
 * refused, failed) is named. */
export function noticeWorthy(v: VenueReadiness): boolean {
  return !v.readable && v.state !== 'loading';
}

export function VenueCoverageNotice({ kalshi, poly, us, onRetryUs, retryingUs = false, onRetryKalshi }: {
  kalshi: VenueReadiness;
  poly: VenueReadiness;
  us?: VenueReadiness;
  /** Explicit re-read of the US account list after a failure (#1006). */
  onRetryUs?: () => void;
  /** That re-read is running: the line stays, its Retry waits (#1060). */
  retryingUs?: boolean;
  /** Explicit re-run of a failed Kalshi key check (#1060). */
  onRetryKalshi?: () => void;
}) {
  const lines = [
    ...(noticeWorthy(kalshi) ? [{ venue: 'Kalshi', id: 'kalshi', v: kalshi }] : []),
    ...(noticeWorthy(poly) ? [{ venue: 'Polymarket', id: 'poly', v: poly }] : []),
    ...(us && usNoticeWorthy(us) ? [{ venue: 'Polymarket US', id: 'us', v: us }] : []),
  ];
  if (lines.length === 0) return null;
  const live = kalshi.readable ? 'Kalshi' : poly.readable ? 'Polymarket' : us?.readable ? 'Polymarket US' : null;
  return (
    <div
      role="status"
      aria-label="Venue coverage"
      data-testid="tracker-venue-notice"
      className="bg-card border-border text-muted-foreground rounded-lg border px-4 py-2 text-xs"
    >
      {lines.map(({ venue, id, v }) => (
        <div key={id} data-testid={`tracker-venue-notice-${id}`} className="flex flex-wrap items-baseline gap-x-2">
          <span className="text-foreground font-medium">{venue} {VENUE_STATE_LABEL[v.state]}</span>
          {v.reason && <span>{v.reason}</span>}
          {id === 'us' && onRetryUs && <RetryButton onClick={onRetryUs} busy={retryingUs} />}
          {id === 'kalshi' && onRetryKalshi && <RetryButton onClick={onRetryKalshi} />}
        </div>
      ))}
      {live && (
        <div className="mt-0.5">{live === 'Polymarket US'
          ? 'Polymarket US saved orders and holdings are available below.'
          : `${live} positions and orders below are live.`}</div>
      )}
    </div>
  );
}

/** US is granted per account, so most logins have no US account at all.
 * Naming "Polymarket US not configured" on every such Tracker would be noise;
 * only a US read that should have worked (failed, refused, retained) is named.
 * The whole-page placeholder still lists US, where every reason matters. */
export function usNoticeWorthy(us: VenueReadiness): boolean {
  return noticeWorthy(us) && us.state !== 'not-configured';
}

/** Whole-page placeholder when no venue can show anything. Names each venue's
 * reason so the next step is visible; it never claims a known-flat account. */
/** While its read runs the button stays in place and keeps focus
 * (`aria-disabled`, not `disabled`, which drops focus), and a press does
 * nothing (#1060). It is as wide in both labels, so its line never re-wraps. */
function RetryButton({ onClick, busy = false }: { onClick: () => void; busy?: boolean }) {
  return (
    <button type="button" onClick={busy ? undefined : onClick} aria-disabled={busy || undefined} aria-busy={busy || undefined}
      // Sized for "Retrying…", so the label change never re-wraps its line.
      className="border-border hover:bg-muted min-w-[4.75rem] rounded border px-1.5 text-center text-xs aria-disabled:opacity-60">
      {busy ? 'Retrying…' : 'Retry'}
    </button>
  );
}

export function VenuePlaceholder({ kalshi, poly, us, onRetryUs, retryingUs = false, onRetryKalshi }: {
  kalshi: VenueReadiness;
  poly: VenueReadiness;
  us?: VenueReadiness;
  onRetryUs?: () => void;
  retryingUs?: boolean;
  onRetryKalshi?: () => void;
}) {
  return (
    <div
      data-testid="tracker-venue-placeholder"
      className="bg-card border-border text-muted-foreground flex flex-col items-center justify-center gap-3 rounded-lg border px-4 py-8 text-center"
    >
      <Crosshair size={48} strokeWidth={1} />
      <h2 className="text-foreground text-lg font-medium">Position Tracker</h2>
      <p className="text-sm">No venue account is available to read positions from.</p>
      <dl className="grid max-w-xl grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-left text-xs">
        <dt className="text-foreground font-medium">Kalshi</dt>
        <dd>
          {VENUE_STATE_LABEL[kalshi.state]}{kalshi.reason ? `: ${kalshi.reason}` : ''}
          {onRetryKalshi && <> <RetryButton onClick={onRetryKalshi} /></>}
        </dd>
        <dt className="text-foreground font-medium">Polymarket</dt>
        <dd>{VENUE_STATE_LABEL[poly.state]}{poly.reason ? `: ${poly.reason}` : ''}</dd>
        {us && <>
          <dt className="text-foreground font-medium">Polymarket US</dt>
          <dd>
            {VENUE_STATE_LABEL[us.state]}{us.reason ? `: ${us.reason}` : ''}
            {onRetryUs && usNoticeWorthy(us) && <> <RetryButton onClick={onRetryUs} busy={retryingUs} /></>}
          </dd>
        </>}
      </dl>
      <p className="text-xs">
        Heads up: Kalshi pauses trading every Thursday 3:00–5:00 AM ET for scheduled
        maintenance — a personal-key connection can fail during that window even when
        your key is fine. Polymarket is unaffected.
      </p>
    </div>
  );
}
